import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  servicePagesEn,
  servicePagesNl,
  servicePageCommonEn,
  servicePageCommonNl,
} from "../i18n/servicePages";
import { staticEn, staticNl } from "../i18n/staticPages";

const I18nContext = createContext(null);
const STORAGE_KEY = "cargo_lang_v1";

const DICTS = {
  en: {
    nav: {
      home: "Home",
      services: "Services",
      tools: "Tools",
      webshop: "Shop",
      pay_online: "Pay Online",
      blog: "Blog",
      about: "About",
      hubs: "Hubs & Locations",
      contact: "Contact",
      get_quote: "Get Quote",
      cart: "Cart",
      become_agent: "Partners",
      my_account: "Mijn 5999Cargo",
    },
    services: {
      air_freight: "Air Freight",
      sea_freight: "Sea Freight",
      moving: "Moving / Relocation",
      shop_drop_ship: "Shop, Drop & Ship",
      shipping_to_curacao: "Shipping to Curaçao",
      air_freight_desc: "Fast 3–5 days",
      sea_freight_desc: "Affordable bulk",
      moving_desc: "Door-to-door",
      shop_drop_ship_desc: "Buy NL, ship CW",
      shipping_to_curacao_desc: "All-in-one guide",
    },
    // Service items shown in the header "Services" dropdown
    service_items: {
      luchtvracht_label: "Air Freight",
      luchtvracht_desc: "5–7 business days — worldwide air freight",
      zeevracht_label: "Sea Freight",
      zeevracht_desc: "Affordable per m³ — worldwide sea freight",
      shop_drop_ship_label: "Shop Drop Ship",
      shop_drop_ship_desc: "Have your webshop orders consolidated & shipped",
      pickup_label: "Pickup",
      pickup_desc: "Pickup & delivery across the Netherlands",
      verhuizing_label: "Moving",
      verhuizing_desc: "Full overseas relocations",
      zakelijk_label: "Business",
      zakelijk_desc: "Custom B2B & e-commerce solutions",
    },
    // Tool items shown in the header "Tools" dropdown
    tool_items: {
      tracking_label: "Track shipment",
      tracking_desc: "Enter your tracking number to view live status",
      documenten_label: "Documents",
      documenten_desc: "Packing lists, invoices, customs forms",
      factuur_label: "Create invoice",
      factuur_desc: "Free invoice / value-declaration PDF",
      paklijst_label: "Packing List",
      paklijst_desc: "Online packing list for your shipment",
      boeking_label: "Booking",
      boeking_desc: "Reserve your shipment in advance",
      btw_label: "VAT",
      btw_desc: "For business exports",
      volumemeter_label: "Volume Meter",
      volumemeter_desc: "Calculate the volume of your shipment",
      cbm_calculator_label: "CBM Calculator",
      cbm_calculator_desc: "Quick calc — cubic meters",
      pickup_calc_label: "Pickup & Delivery calculator",
      pickup_calc_desc: "Estimate pickup costs across the Netherlands",
    },
    home: {
      eyebrow: "Rotterdam → Curaçao",
      h1_a: "Ship anything to",
      h1_b: "the Caribbean",
      h1_c: "from the Netherlands.",
      sub: "Air, sea, moving or Shop-Drop-Ship — handled end-to-end by a team that's been shipping from Rotterdam since 2014. Door-to-door pickup, customs clearance and on-time delivery.",
      cta_quote: "Get an instant quote",
      cta_services: "Explore services",
      stat_shipments: "Shipments / year",
      stat_experience: "Experience",
      stat_rating: "Customer rating",
      trust_insured: "Insured shipments",
      trust_response: "1h quote response",
      trust_pickup: "Pickup nationwide NL",
      trust_boxes: "Boxes & packaging in-store",
      services_eyebrow: "/ Services",
      services_title_a: "Every shipping need,",
      services_title_b: "handled from one place.",
      services_help: "Need help choosing? Talk to us →",
      learn_more: "Learn more",
      how_title: "From your door in NL to their door in Curaçao.",
      step1_title: "Request a quote",
      step1_desc: "Tell us what you're shipping. We respond within 1 business hour with a transparent price.",
      step2_title: "We pick up",
      step2_desc: "Our team collects from any Dutch address. Or drop your boxes at our Rotterdam warehouse.",
      step3_title: "Ship & clear",
      step3_desc: "We consolidate, ship by air or sea, and handle Curaçao customs end-to-end.",
      step4_title: "Doorstep delivery",
      step4_desc: "Last-mile delivery to your recipient on the island. Confirmation by WhatsApp.",
    },
    quote: {
      title: "Get an instant quote",
      subtitle: "Reply within 1 business hour. No obligation.",
      name: "Full name",
      email: "Email",
      phone: "Phone",
      service: "Service",
      destination: "Destination",
      weight: "Approx. weight or volume",
      tell_more: "Tell us more",
      submit: "Get my quote →",
      sending: "Sending...",
      consent: "By submitting you agree to be contacted by 5999Cargo about your shipment.",
      success_title: "Quote requested!",
      success_desc: "A specialist will contact you within 1 business hour with your tailored quote.",
      submit_another: "Submit another",
    },
    cta: {
      ready_title: "Ready to ship to Curaçao?",
      ready_sub: "Get a quote in under 60 seconds. No obligation, no hidden fees.",
      shop_boxes: "Shop boxes",
      get_quote: "Get my quote",
    },
    faq: {
      title: "Frequently Asked Questions",
    },
    testimonials: {
      eyebrow: "/ Trust",
      title_a: "Trusted by senders",
      title_b: "across Europe",
      summary: "4.9 / 5 — 1,200+ shipments",
    },
    footer: {
      tagline: "5999Cargo Group Europe is your logistics partner for air freight, sea freight, Shop-Drop-Ship, pickup & delivery, business shipments and international moves from the Netherlands to Curaçao, Bonaire, Aruba, Sint Maarten, Suriname and other destinations.",
      services: "Services",
      company: "Company",
      hq: "Headquarters",
      whatsapp_us: "WhatsApp Us",
      rights: "All rights reserved.",
    },
    common: {
      back_home: "← Back to home",
      required_field: "This field is required.",
      invalid_email: "Please enter a valid email address.",
      submitting: "Submitting…",
      sending: "Sending…",
      try_again: "Please try again.",
    },
    forms: {
      sender_block_title: "Sender",
      receiver_block_title: "Receiver",
      first_name: "First name",
      last_name: "Last name",
      company_optional: "Company name (optional)",
      email: "Email address",
      phone: "Phone number",
      whatsapp_optional: "WhatsApp number (optional)",
      sender_address: "Sender address",
      postcode: "Postcode",
      city: "City",
      country: "Country",
      recipient_name: "Receiver name",
      recipient_email_optional: "Email address (optional)",
      destination_address: "Destination address",
      destination_city: "Destination city",
      destination_country: "Destination country",
      extra_instructions_optional: "Extra instructions (optional)",
      shipment_block_title: "Shipment details",
      destination: "Destination",
      shipment_method: "Shipping method",
      w_number_optional: "W-number (optional)",
      remark: "Remark",
      truthful_required: "The information I provided is truthful and complete.",
      consent_required: "I agree that 5999Cargo may contact me about this booking.",
      consents_required_error: "Both consent checkboxes are required.",
    },
    dangerous_goods: {
      paklijst_warning_title: "Check whether your goods may be shipped",
      boeking_warning_title: "Prohibited items and dangerous goods",
      warning_intro: "Certain goods are prohibited or classified as Dangerous Goods (e.g. aerosols, batteries, perfume, paint, flammable liquids, magnets, drugs, weapons, live animals).",
      full_list_link: "View the full list",
      checkbox_label: "I confirm that my shipment does not contain prohibited items, dangerous goods, or goods that may not be transported without prior approval.",
      checkbox_required_error: "You must confirm that your shipment does not contain prohibited items or dangerous goods before submitting the form.",
      required_prefix: "Required",
    },
    paklijst: {
      page_title: "Online packing list for customs & shipping",
      page_subtitle: "Submit your packing list online — fast and secure. Required for customs clearance, value declaration and correct shipping to the Caribbean.",
      submit: "Submit packing list",
      submitting: "Submitting…",
      success_title: "Packing list received!",
      success_desc: "We've forwarded your packing list to our team. You'll receive a confirmation by email shortly.",
      submit_new: "Submit a new packing list",
      add_row: "Add row",
      box_number: "Item #",
      description: "Description",
      quantity: "Qty",
      value: "Value (€)",
      condition: "Condition",
      condition_new: "New",
      condition_used: "Used",
      method_zee: "Sea freight",
      method_lucht: "Air freight",
      method_shop_drop_ship: "Shop, Drop & Ship",
      method_verhuizing: "Moving",
      min_one_row_error: "Please add at least one packing list row with a description.",
      invoice_option_label: "I also want to generate and download an invoice/value declaration.",
      invoice_option_help: "Based on the information and items entered, the system will automatically create an invoice/value declaration. Please make sure all descriptions and values are correct.",
      invoice_items_value_required: "When generating an invoice every item must have a value (€).",
      invoice_recipient_country_required: "Recipient country is required to generate the invoice.",
      invoice_generated_title: "Invoice / value declaration created",
      invoice_generated_intro: "Use the button below to download your invoice. A copy is automatically sent to 5999Cargo.",
      invoice_number_label: "Invoice number",
      invoice_download_btn: "Download invoice/value declaration",
      invoice_downloading: "Downloading PDF…",
      invoice_failed_title: "Invoice/value declaration could not be created",
      invoice_failed_generic: "Your form has been submitted, but the invoice could not be auto-generated. Please contact us so we can send it manually.",
    },
    boeking: {
      page_title: "Booking form — Reserve your cargo shipment",
      page_subtitle: "Reserve your shipment in advance and provide all details. Pickup, documents, sender/receiver and Dangerous Goods declaration in one form.",
      submit: "Submit booking",
      submitting: "Submitting…",
      success_title: "Booking received!",
      success_desc: "We've received your booking. You'll receive a confirmation by email shortly.",
      submit_new: "Submit a new booking",
      shipment_type: "Shipment type",
      type_pakket: "Parcel",
      type_pallet: "Pallet",
      type_groupage: "Groupage / mixed",
      type_verhuizing: "Move",
      boxes: "Number of boxes",
      pallets: "Number of pallets",
      estimated_weight: "Estimated weight (kg)",
      estimated_cbm: "Estimated CBM (m³)",
      goods_description: "Goods description",
      goods_value: "Goods value (€)",
      pickup_section: "Pickup",
      pickup_required_label: "Pickup requested",
      pickup_address: "Pickup address",
      pickup_datetime: "Preferred date/time",
      consent_section: "Confirmation",
      invoice_option_label: "I also want to generate and download an invoice/value declaration.",
      invoice_option_help: "Based on the information entered, the system will automatically create an invoice/value declaration. Please make sure the goods description and goods value are correct.",
      invoice_goods_value_required: "Please enter a goods value before requesting an invoice/value declaration.",
      invoice_generated_title: "Invoice / value declaration created",
      invoice_generated_intro: "Use the button below to download your invoice. A copy is automatically sent to 5999Cargo.",
      invoice_number_label: "Invoice number",
      invoice_download_btn: "Download invoice/value declaration",
      invoice_downloading: "Downloading PDF…",
      invoice_failed_title: "Invoice/value declaration could not be created",
      invoice_failed_generic: "Your booking has been submitted, but the invoice could not be auto-generated. Please contact us so we can send it manually.",
    },
    pay_online: {
      seo_title: "Pay Online for your shipment | 5999Cargo",
      seo_description: "Securely pay your invoice or shipment costs online through 5999Cargo using your W-number or tracking number. No payment = no shipping.",
      page_title: "Online Payment",
      intro_p1: "Through this page you can securely complete the payment of your invoice or shipment costs.",
      intro_p2: "Timely payment prevents delays.",
    },
    pickup_calc: {
      title: "Pickup & Delivery calculator",
      heading: "Calculate your pickup cost",
      intro: "Indicative price for collecting your shipment in the Netherlands. Local (≤ 25 km from Rotterdam) or nationwide.",
      distance_label: "Distance from Rotterdam (km)",
      volume_label: "Volume category",
      city_label: "City (optional)",
      postcode_label: "Postcode (optional)",
      boxes_label: "Number of boxes (optional)",
      note_label: "Note (optional)",
      note_placeholder: "e.g. upper floor pickup, narrow street…",
      result_indication: "Indicative quote",
      tariff_type: "Tariff",
      single_distance: "One-way distance from Rotterdam",
      return_distance: "Return distance used for calculation",
      base_rate: "Base rate",
      km_cost: "Kilometre fee",
      cta: "Schedule pickup appointment",
      disclaimer: "This calculator gives an indication only. Final costs may differ depending on accessibility, waiting time, extra handling, floor, parking distance or other shipment specifics.",
      negative_error: "Distance must be 0 km or higher.",
      empty_state: "Fill in the distance and volume to see your indicative quote.",
      vol_to_half: "Loose boxes up to 0.5 m³",
      vol_half_to_one: "0.5 m³ to 1 m³",
      vol_from_one: "From 1 m³",
      local: "local",
      national: "nationwide",
      help_radius: "Local within 25 km of Rotterdam · Nationwide outside that.",
      help_formula: "For nationwide: base rate + (km × 2) × € 0.44 — return trip included.",
    },
    factuur: {
      title: "Create invoice / value declaration",
      eyebrow: "Free tool",
      lead: "Generate a professional invoice & value declaration PDF for your shipment in under two minutes. A copy is automatically sent to 5999Cargo.",
      meta_title: "Create invoice — Free invoice & value-declaration generator",
      meta_description: "Generate a professional invoice or value declaration for your shipment to Curaçao, Aruba, Bonaire or the Caribbean. Bilingual, PDF download, copy to office.",
      lang_section: "Document language",
      lang_nl: "Dutch",
      lang_en: "English",
      currency_label: "Currency",
      invoice_date: "Invoice date",
      logo_section: "Customer logo (optional)",
      logo_help: "PNG, JPG or WEBP — max 2 MB. Will appear in the invoice header.",
      logo_upload: "Upload logo",
      logo_clear: "Remove logo",
      logo_uploaded: "Logo uploaded",
      sender: "Sender",
      receiver: "Receiver",
      shipment: "Shipment",
      field_name: "Name",
      field_company: "Company",
      field_address: "Address",
      field_postcode: "Postcode",
      field_city: "City",
      field_country: "Country",
      field_email: "E-mail",
      field_phone: "Phone",
      field_destination: "Destination",
      field_w_number: "W-number",
      field_description: "Description",
      field_boxes: "Number of packages",
      field_remarks: "Remarks",
      lines_title: "Invoice lines",
      add_line: "Add line",
      col_desc: "Description",
      col_qty: "Quantity",
      col_unit: "Unit price",
      col_total: "Total",
      remove_line: "Remove line",
      vat_label: "VAT %",
      subtotal: "Subtotal",
      vat_amount: "VAT amount",
      total: "Total",
      declarations_title: "Required declarations",
      decl_value: "I confirm this invoice may be used as a value declaration for the shipment.",
      decl_truthful: "I confirm all information has been entered truthfully.",
      submit: "Generate PDF",
      generating: "Generating PDF…",
      success_title: "Invoice generated",
      success_intro: "Your invoice has been downloaded and a copy was sent to 5999Cargo.",
      success_number: "Invoice number",
      success_email_sent: "Copy e-mailed to 5999Cargo: yes",
      success_email_skipped: "Copy could not be e-mailed (no Resend key configured). The office still keeps the PDF on file.",
      new_invoice: "Create another invoice",
      err_logo_failed: "Logo upload failed.",
      err_submit_failed: "Could not generate the invoice.",
      err_at_least_one_line: "At least one invoice line is required.",
      err_missing_required: "Please complete all required fields.",
      err_declarations: "Both declarations must be accepted before generating the PDF.",
      hint_pdf_download: "The PDF will start downloading automatically after submit.",
      hint_generating: "Please hold on, your PDF is being prepared.",
    },
  },
  nl: {
    nav: {
      home: "Home",
      services: "Diensten",
      tools: "Tools",
      webshop: "Shop",
      pay_online: "Online betalen",
      blog: "Blog",
      about: "Over ons",
      hubs: "Hubs & Locaties",
      contact: "Contact",
      get_quote: "Offerte aanvragen",
      cart: "Winkelmand",
      become_agent: "Partners",
      my_account: "Mijn 5999Cargo",
    },
    services: {
      air_freight: "Luchtvracht",
      sea_freight: "Zeevracht",
      moving: "Verhuizing",
      shop_drop_ship: "Shop, Drop & Ship",
      shipping_to_curacao: "Verzenden naar Curaçao",
      air_freight_desc: "Snel 3–5 dagen",
      sea_freight_desc: "Voordelig in bulk",
      moving_desc: "Deur tot deur",
      shop_drop_ship_desc: "Koop NL, verzend CW",
      shipping_to_curacao_desc: "Compleet overzicht",
    },
    service_items: {
      luchtvracht_label: "Luchtvracht",
      luchtvracht_desc: "5–7 werkdagen — wereldwijde luchtvracht",
      zeevracht_label: "Zeevracht",
      zeevracht_desc: "Voordelig per m³ — wereldwijde zeevracht",
      shop_drop_ship_label: "Shop Drop Ship",
      shop_drop_ship_desc: "Laat webshop-bestellingen verzamelen & verzenden",
      pickup_label: "Pickup",
      pickup_desc: "Ophalen & leveren door heel Nederland",
      verhuizing_label: "Verhuizing",
      verhuizing_desc: "Volledige verhuizingen overzee",
      zakelijk_label: "Zakelijk",
      zakelijk_desc: "Maatwerk voor B2B & e-commerce",
    },
    tool_items: {
      tracking_label: "Zending volgen",
      tracking_desc: "Voer uw trackingnummer in om uw zending te volgen",
      documenten_label: "Documenten",
      documenten_desc: "Paklijsten, facturen, douaneformulieren",
      factuur_label: "Factuur maken",
      factuur_desc: "Gratis factuur / waardeverklaring als PDF",
      paklijst_label: "Paklijst",
      paklijst_desc: "Online paklijst voor uw zending",
      boeking_label: "Boeking",
      boeking_desc: "Reserveer uw zending vooraf",
      btw_label: "BTW",
      btw_desc: "Voor zakelijke exporten",
      volumemeter_label: "Volumemeter",
      volumemeter_desc: "Bereken het volume van uw zending",
      cbm_calculator_label: "CBM Calculator",
      cbm_calculator_desc: "Snel rekenen — kubieke meters",
      pickup_calc_label: "Pickup & Delivery calculator",
      pickup_calc_desc: "Bereken pickup-kosten in Nederland",
    },
    home: {
      eyebrow: "Cargo Nederland → Caribbean",
      h1_a: "Cargo naar Curaçao,",
      h1_b: "Aruba & het Caribisch gebied",
      h1_c: "online geregeld.",
      sub: "Particulier of zakelijk — zee- en luchtvracht, verhuizingen, Shop, Drop & Ship en pickup vanuit heel Nederland. Met online paklijst, boekingsformulier, CBM-calculator, pickup-calculator en Mijn 5999Cargo regelt u uw zending overzichtelijk en zonder verrassingen.",
      cta_quote: "Direct offerte aanvragen",
      cta_services: "Bekijk onze diensten",
      stat_shipments: "Zendingen per jaar",
      stat_experience: "Jaar ervaring",
      stat_rating: "Klantbeoordeling",
      trust_insured: "Verzekerde zendingen",
      trust_response: "Reactie binnen 1 werkuur",
      trust_pickup: "Pickup heel Nederland",
      trust_boxes: "Online tools & Mijn 5999Cargo",
      services_eyebrow: "/ Diensten",
      services_title_a: "Een complete cargo-partner —",
      services_title_b: "vanuit één plek geregeld.",
      services_help: "Onzeker over de beste route? Neem contact op →",
      learn_more: "Meer informatie",
      how_title: "Van uw deur in Nederland naar de ontvanger op het eiland — in vier stappen.",
      step1_title: "Vraag online een offerte aan",
      step1_desc: "Vertel ons wat u verstuurt of vul direct het boekingsformulier in. Reactie binnen 1 werkuur met een transparante prijs.",
      step2_title: "Pickup of inlevering",
      step2_desc: "Wij halen op via heel Nederland of u levert in bij ons depot in Rotterdam. Bereken vooraf indicatief de pickup-kosten.",
      step3_title: "Documentencontrole & verzenden",
      step3_desc: "Wij controleren paklijst, factuur en exportdocumenten vóór vertrek. Géén betaling of documenten? Géén verzending. Zo voorkomen we vertraging.",
      step4_title: "Bezorging op bestemming",
      step4_desc: "Levering bij de ontvanger op Curaçao, Aruba, Bonaire, Sint Maarten of Suriname. Statusupdates via Mijn 5999Cargo en WhatsApp.",
    },
    quote: {
      title: "Direct een offerte",
      subtitle: "Antwoord binnen 1 werkuur. Vrijblijvend.",
      name: "Volledige naam",
      email: "E-mail",
      phone: "Telefoon",
      service: "Dienst",
      destination: "Bestemming",
      weight: "Geschat gewicht of volume",
      tell_more: "Vertel ons meer",
      submit: "Mijn offerte →",
      sending: "Verzenden...",
      consent: "Door te verzenden geef je toestemming dat 5999Cargo contact met je opneemt over je zending.",
      success_title: "Offerte aangevraagd!",
      success_desc: "Een specialist neemt binnen 1 werkuur contact op met jouw offerte.",
      submit_another: "Nieuwe aanvraag",
    },
    cta: {
      ready_title: "Klaar om naar Curaçao te verzenden?",
      ready_sub: "Krijg een offerte in minder dan 60 seconden. Geen verplichtingen, geen verborgen kosten.",
      shop_boxes: "Dozen kopen",
      get_quote: "Mijn offerte",
    },
    faq: {
      title: "Veelgestelde vragen",
    },
    testimonials: {
      eyebrow: "/ Vertrouwen",
      title_a: "Vertrouwd door verzenders",
      title_b: "in heel Europa",
      summary: "4.9 / 5 — 1.200+ zendingen",
    },
    footer: {
      tagline: "5999Cargo Group Europe is uw logistieke partner voor luchtvracht, zeevracht, Shop, Drop & Ship, pickup & delivery, zakelijke zendingen en internationale verhuizingen vanuit Nederland naar Curaçao, Bonaire, Aruba, Sint Maarten, Suriname en andere bestemmingen.",
      services: "Diensten",
      company: "Bedrijf",
      hq: "Hoofdkantoor",
      whatsapp_us: "App ons",
      rights: "Alle rechten voorbehouden.",
    },
    common: {
      back_home: "← Terug naar home",
      required_field: "Dit veld is verplicht.",
      invalid_email: "Vul een geldig e-mailadres in.",
      submitting: "Bezig met verzenden…",
      sending: "Verzenden…",
      try_again: "Probeer opnieuw.",
    },
    forms: {
      sender_block_title: "Verzender",
      receiver_block_title: "Ontvanger",
      first_name: "Voornaam",
      last_name: "Achternaam",
      company_optional: "Bedrijfsnaam (optioneel)",
      email: "E-mailadres",
      phone: "Telefoonnummer",
      whatsapp_optional: "WhatsApp nummer (optioneel)",
      sender_address: "Adres verzender",
      postcode: "Postcode",
      city: "Stad",
      country: "Land",
      recipient_name: "Naam ontvanger",
      recipient_email_optional: "E-mailadres (optioneel)",
      destination_address: "Adres bestemming",
      destination_city: "Stad bestemming",
      destination_country: "Land bestemming",
      extra_instructions_optional: "Extra instructies (optioneel)",
      shipment_block_title: "Zendinggegevens",
      destination: "Bestemming",
      shipment_method: "Verzendmethode",
      w_number_optional: "W-nummer (optioneel)",
      remark: "Opmerking",
      truthful_required: "De door mij verstrekte informatie is naar waarheid ingevuld.",
      consent_required: "Ik ga ermee akkoord dat 5999Cargo mij over deze boeking kan contacteren.",
      consents_required_error: "Beide akkoord-vinkjes zijn verplicht.",
    },
    dangerous_goods: {
      paklijst_warning_title: "Controleer of uw goederen verzonden mogen worden",
      boeking_warning_title: "Verboden goederen en gevaarlijke stoffen",
      warning_intro: "Bepaalde goederen zijn verboden of vallen onder Dangerous Goods (bv. spuitbussen, batterijen, parfum, verf, brandbare vloeistoffen, magneten, drugs, wapens, levende dieren).",
      full_list_link: "Bekijk de volledige lijst",
      checkbox_label: "Ik verklaar dat mijn zending geen verboden goederen, gevaarlijke stoffen of goederen bevat die zonder voorafgaande toestemming niet vervoerd mogen worden.",
      checkbox_required_error: "U moet bevestigen dat uw zending geen verboden goederen of gevaarlijke stoffen bevat voordat u het formulier kunt verzenden.",
      required_prefix: "Verplicht",
    },
    paklijst: {
      page_title: "Paklijst invullen voor douane en verzending",
      page_subtitle: "Vul eenvoudig uw paklijst online in voor pakketten, zeevracht en luchtvracht naar Curaçao, Aruba, Bonaire of de Caribbean — nodig voor douane, waarde-opgave en correcte verzending.",
      submit: "Paklijst verzenden",
      submitting: "Bezig met verzenden…",
      success_title: "Paklijst ontvangen!",
      success_desc: "Wij hebben uw paklijst doorgestuurd naar ons team. U ontvangt zo een bevestiging per e-mail.",
      submit_new: "Nieuwe paklijst indienen",
      add_row: "Regel toevoegen",
      box_number: "Artikel #",
      description: "Omschrijving",
      quantity: "Aantal",
      value: "Waarde (€)",
      condition: "Staat",
      condition_new: "Nieuw",
      condition_used: "Gebruikt",
      method_zee: "Zeevracht",
      method_lucht: "Luchtvracht",
      method_shop_drop_ship: "Shop, Drop & Ship",
      method_verhuizing: "Verhuizing",
      min_one_row_error: "Voeg minimaal één paklijst-regel met omschrijving toe.",
      invoice_option_label: "Ik wil ook direct een factuur/waardeverklaring laten genereren en downloaden.",
      invoice_option_help: "Op basis van de ingevulde gegevens en opgegeven goederen maakt het systeem automatisch een factuur/waardeverklaring aan. Controleer goed of alle omschrijvingen en waardes correct zijn ingevuld.",
      invoice_items_value_required: "Voor het aanmaken van een factuur moet elke regel een waarde (€) bevatten.",
      invoice_recipient_country_required: "Land van de ontvanger is verplicht voor de factuur/waardeverklaring.",
      invoice_generated_title: "Factuur / waardeverklaring aangemaakt",
      invoice_generated_intro: "Gebruik de knop hieronder om uw factuur te downloaden. Een kopie is automatisch naar 5999Cargo gestuurd.",
      invoice_number_label: "Factuurnummer",
      invoice_download_btn: "Download factuur/waardeverklaring",
      invoice_downloading: "PDF wordt gedownload…",
      invoice_failed_title: "Factuur/waardeverklaring kon niet worden aangemaakt",
      invoice_failed_generic: "Uw formulier is verzonden, maar de factuur/waardeverklaring kon niet automatisch worden gegenereerd. Neem contact met ons op zodat wij deze handmatig kunnen toesturen.",
    },
    boeking: {
      page_title: "Boekingsformulier — Reserveer uw cargo-zending",
      page_subtitle: "Reserveer online uw cargo-zending naar de Caribbean — pickup, documenten, sender/receiver en Dangerous Goods verklaring in één formulier.",
      submit: "Boeking verzenden",
      submitting: "Bezig met verzenden…",
      success_title: "Boeking ontvangen!",
      success_desc: "Wij hebben uw boeking ontvangen. U ontvangt zo een bevestiging per e-mail.",
      submit_new: "Nieuwe boeking indienen",
      shipment_type: "Type zending",
      type_pakket: "Pakket",
      type_pallet: "Pallet",
      type_groupage: "Groupage / gemengd",
      type_verhuizing: "Verhuizing",
      boxes: "Aantal dozen",
      pallets: "Aantal pallets",
      estimated_weight: "Geschat gewicht (kg)",
      estimated_cbm: "Geschat CBM (m³)",
      goods_description: "Omschrijving goederen",
      goods_value: "Waarde goederen (€)",
      pickup_section: "Pickup",
      pickup_required_label: "Pickup gewenst",
      pickup_address: "Ophaaladres",
      pickup_datetime: "Gewenste datum/tijd",
      consent_section: "Akkoord",
      invoice_option_label: "Ik wil ook direct een factuur/waardeverklaring laten genereren en downloaden.",
      invoice_option_help: "Op basis van de ingevulde gegevens maakt het systeem automatisch een factuur/waardeverklaring aan. Controleer goed of de omschrijving en waarde van de goederen correct zijn ingevuld.",
      invoice_goods_value_required: "Vul de waarde van de goederen in om een factuur/waardeverklaring te kunnen genereren.",
      invoice_generated_title: "Factuur / waardeverklaring aangemaakt",
      invoice_generated_intro: "Gebruik de knop hieronder om uw factuur te downloaden. Een kopie is automatisch naar 5999Cargo gestuurd.",
      invoice_number_label: "Factuurnummer",
      invoice_download_btn: "Download factuur/waardeverklaring",
      invoice_downloading: "PDF wordt gedownload…",
      invoice_failed_title: "Factuur/waardeverklaring kon niet worden aangemaakt",
      invoice_failed_generic: "Uw boeking is verzonden, maar de factuur kon niet automatisch worden gegenereerd. Neem contact met ons op zodat wij deze handmatig kunnen toesturen.",
    },
    pay_online: {
      seo_title: "Online betalen voor uw zending | 5999Cargo",
      seo_description: "Betaal uw zending, factuur of openstaande kosten veilig online via 5999Cargo met uw W-nummer of trackingnummer. Geen betaling = geen verzending.",
      page_title: "Online betalen",
      intro_p1: "Via deze pagina kunt u veilig uw factuur, zending of openstaande kosten betalen.",
      intro_p2: "Tijdige betaling voorkomt vertraging.",
    },
    pickup_calc: {
      title: "Pickup & Delivery calculator",
      heading: "Bereken uw pickup-kosten",
      intro: "Indicatieve prijs voor het laten ophalen van uw zending in Nederland. Lokaal (≤ 25 km vanaf Rotterdam) of landelijk.",
      distance_label: "Afstand vanaf Rotterdam (km)",
      volume_label: "Volume categorie",
      city_label: "Plaats (optioneel)",
      postcode_label: "Postcode (optioneel)",
      boxes_label: "Aantal dozen (optioneel)",
      note_label: "Opmerking (optioneel)",
      note_placeholder: "bv. afhalen bovenverdieping, smalle straat…",
      result_indication: "Indicatie",
      tariff_type: "Tarief",
      single_distance: "Enkele afstand vanaf Rotterdam",
      return_distance: "Retourafstand voor berekening",
      base_rate: "Basistarief",
      km_cost: "Kilometervergoeding",
      cta: "Plan pickup afspraak",
      disclaimer: "De calculator geeft een indicatie. Definitieve kosten kunnen afwijken door bereikbaarheid, wachttijd, extra handling, verdieping, parkeerafstand of afwijkende zending.",
      negative_error: "Afstand moet 0 km of hoger zijn.",
      empty_state: "Vul de afstand en volume in om uw indicatie te zien.",
      vol_to_half: "Losse dozen tot 0,5 m³",
      vol_half_to_one: "0,5 m³ tot 1 m³",
      vol_from_one: "Vanaf 1 m³",
      local: "lokaal",
      national: "landelijk",
      help_radius: "Lokaal binnen 25 km van Rotterdam · Landelijk daarbuiten.",
      help_formula: "Bij landelijk: basistarief + (km × 2) × € 0,44 — we rekenen heen + terug.",
    },
    factuur: {
      title: "Factuur / waardeverklaring maken",
      eyebrow: "Gratis tool",
      lead: "Maak in minder dan twee minuten een professionele factuur & waardeverklaring als PDF voor uw zending. Een kopie gaat automatisch naar 5999Cargo.",
      meta_title: "Factuur maken — Gratis invoice & waardeverklaring generator",
      meta_description: "Maak gratis een professionele factuur of waardeverklaring voor uw zending naar Curaçao, Aruba, Bonaire of de Caribbean. Tweetalig, PDF-download, kopie naar het kantoor.",
      lang_section: "Documenttaal",
      lang_nl: "Nederlands",
      lang_en: "Engels",
      currency_label: "Valuta",
      invoice_date: "Factuurdatum",
      logo_section: "Klantlogo (optioneel)",
      logo_help: "PNG, JPG of WEBP — max 2 MB. Verschijnt in de factuurkop.",
      logo_upload: "Logo uploaden",
      logo_clear: "Logo verwijderen",
      logo_uploaded: "Logo geüpload",
      sender: "Afzender",
      receiver: "Ontvanger",
      shipment: "Zending",
      field_name: "Naam",
      field_company: "Bedrijf",
      field_address: "Adres",
      field_postcode: "Postcode",
      field_city: "Plaats",
      field_country: "Land",
      field_email: "E-mail",
      field_phone: "Telefoon",
      field_destination: "Bestemming",
      field_w_number: "W-nummer",
      field_description: "Omschrijving",
      field_boxes: "Aantal pakketten",
      field_remarks: "Opmerking",
      lines_title: "Factuurregels",
      add_line: "Regel toevoegen",
      col_desc: "Omschrijving",
      col_qty: "Aantal",
      col_unit: "Eenheidsprijs",
      col_total: "Totaal",
      remove_line: "Regel verwijderen",
      vat_label: "BTW %",
      subtotal: "Subtotaal",
      vat_amount: "BTW-bedrag",
      total: "Totaal",
      declarations_title: "Verplichte verklaringen",
      decl_value: "Ik bevestig dat deze factuur als waardeverklaring voor de zending mag worden gebruikt.",
      decl_truthful: "Ik bevestig dat alle gegevens naar waarheid zijn ingevuld.",
      submit: "Genereer PDF",
      generating: "PDF wordt aangemaakt…",
      success_title: "Factuur aangemaakt",
      success_intro: "Uw factuur is gedownload en een kopie is naar 5999Cargo gestuurd.",
      success_number: "Factuurnummer",
      success_email_sent: "Kopie ge-e-maild naar 5999Cargo: ja",
      success_email_skipped: "Kopie kon niet ge-e-maild worden (geen Resend-key ingesteld). Het kantoor bewaart de PDF wel.",
      new_invoice: "Nieuwe factuur maken",
      err_logo_failed: "Logo upload mislukt.",
      err_submit_failed: "De factuur kon niet worden aangemaakt.",
      err_at_least_one_line: "Minimaal één factuurregel is verplicht.",
      err_missing_required: "Vul a.u.b. alle verplichte velden in.",
      err_declarations: "Beide verklaringen moeten zijn aangevinkt voordat de PDF gegenereerd wordt.",
      hint_pdf_download: "De PDF wordt automatisch gedownload na verzending.",
      hint_generating: "Even geduld, uw PDF wordt voorbereid.",
    },
  },
};

const SUPPORTED = [
  { code: "en", label: "EN", name: "English" },
  { code: "nl", label: "NL", name: "Nederlands" },
];

// Merge service-page dictionaries into the main DICTS object.
DICTS.en.service_pages = servicePagesEn;
DICTS.en.service_common = servicePageCommonEn;
DICTS.nl.service_pages = servicePagesNl;
DICTS.nl.service_common = servicePageCommonNl;

// Merge static-page dictionaries
Object.assign(DICTS.en, staticEn);
Object.assign(DICTS.nl, staticNl);

function getNested(obj, path) {
  return path.split(".").reduce((o, k) => (o && o[k] !== undefined ? o[k] : undefined), obj);
}

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && DICTS[stored]) return stored;
    } catch (e) {
      console.warn("I18nContext: localStorage read failed", e);
    }
    if (typeof navigator !== "undefined" && navigator.language?.startsWith("nl")) return "nl";
    return "en";
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
      document.documentElement.lang = lang;
    } catch (e) {
      console.warn("I18nContext: localStorage write failed", e);
    }
  }, [lang]);

  const t = useMemo(() => {
    return (key, fallback = "") => {
      const v = getNested(DICTS[lang], key);
      if (v !== undefined) return v;
      const en = getNested(DICTS.en, key);
      return en !== undefined ? en : fallback || key;
    };
  }, [lang]);

  // Returns a whole sub-tree (used for arrays/objects like service_pages.air_freight)
  const tNode = useMemo(() => {
    return (key) => {
      const v = getNested(DICTS[lang], key);
      if (v !== undefined) return v;
      return getNested(DICTS.en, key);
    };
  }, [lang]);

  const value = useMemo(
    () => ({ lang, setLang, t, tNode, languages: SUPPORTED }),
    [lang, t, tNode]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be inside I18nProvider");
  return ctx;
};
